import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { getCookieValue } from "src/constants/functions";
import { logout } from "src/constants/global-const";
import { tokenStore } from "./tokenStore";
import { getOrCreateRefresh } from "./refreshLock";

// Extend Axios config type to include our custom `_retry` flag
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

type AuthHeaderSetter = (headers: InternalAxiosRequestConfig["headers"]) => void;

// Default: corp/OMS APIs expect `Bearer <accessToken>`.
const setBearerAccessToken: AuthHeaderSetter = (headers) => {
  const newToken = tokenStore.getAccessToken();
  if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
};

/**
 * Attaches a response interceptor that transparently handles token expiry:
 *
 *  1. Request fails with HTTP 401 or 403 (access token expired / invalid).
 *  2. `getOrCreateRefresh` is invoked — if another caller (interceptor or
 *     route-change hook) already kicked off a refresh, this one awaits the
 *     same in-flight promise instead of triggering a duplicate.
 *  3. On refresh success: tokenStore is updated, the original request's
 *     Authorization header is rewritten, and the request is retried.
 *  4. On refresh failure: tokens are cleared and the user is logged out.
 *  5. Infinite-loop guard: the `_retry` flag ensures a request is only
 *     retried once. A second 401/403 on the retry means the new token is
 *     also bad → go straight to logout.
 *
 * `setAuthHeader` lets APIs that expect a different credential (e.g. the
 * Medvol APIs, which take the idToken — see `setIdTokenHeader`) rewrite the
 * retried request's header correctly. Defaults to `Bearer <accessToken>`.
 */
export const attachRefreshInterceptor = (instance: AxiosInstance, setAuthHeader: AuthHeaderSetter = setBearerAccessToken) => {
  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error?.response?.status;

    // Handle 401 (Unauthorized) and 403 (Forbidden) — the backend uses
    // either to signal an expired/invalid access token. Any other status
    // passes through untouched.
    if ((status !== 401 && status !== 403) || !originalRequest) {
      return Promise.reject(error);
    }

    // Guard against infinite retry: if this request was already retried
    // once, the freshly minted token is also invalid → force logout.
    if (originalRequest._retry) {
      tokenStore.clearTokens();
      logout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      await getOrCreateRefresh();

      if (originalRequest.headers) {
        setAuthHeader(originalRequest.headers);
      }
      return instance(originalRequest);
    } catch (refreshError) {
      tokenStore.clearTokens();
      logout();
      return Promise.reject(refreshError);
    }
  });
};

/**
 * `validateStatus` for calls that want 4xx responses resolved (so the caller
 * can read the error body) but still need 401/403 to reject so the refresh
 * interceptor above can run.
 */
export const acceptExceptAuthErrors = (status: number) =>
  status >= 200 && status < 500 && status !== 401 && status !== 403;

/** Same as `acceptExceptAuthErrors`, but also resolves 500. */
export const acceptExceptAuthErrorsIncl500 = (status: number) =>
  status >= 200 && status <= 500 && status !== 401 && status !== 403;

// ─── idToken-authenticated APIs (Medvol / external) ─────────────────────────
// These APIs take the Cognito idToken in `Authorization` instead of
// `Bearer <accessToken>`. Refresh only updates the in-memory store, so the
// `idToken` cookie goes stale after the first refresh — always read through
// `getIdToken()`.

/** Current idToken: in-memory (kept fresh by refresh) first, login cookie as fallback. */
export const getIdToken = (): string | null => tokenStore.getIdToken() || getCookieValue("idToken");

/**
 * Retry-header setter for idToken APIs. Keeps the original format — raw
 * token, or `Bearer <idToken>` where the request used that prefix.
 */
export const setIdTokenHeader: AuthHeaderSetter = (headers) => {
  const idToken = tokenStore.getIdToken();
  if (!idToken) return;
  const previous = String(headers["Authorization"] ?? "");
  headers["Authorization"] = previous.startsWith("Bearer ") ? `Bearer ${idToken}` : idToken;
};

/** Shared client for idToken APIs that aren't on a dedicated instance. */
export const idTokenClient: AxiosInstance = axios.create();
attachRefreshInterceptor(idTokenClient, setIdTokenHeader);

// ─── Attach to the global axios instance ────────────────────────────────────
// Many files across the codebase use raw `axios.get/post/...` calls instead
// of a pre-configured instance. Attaching here ensures those calls also get
// 401/403 refresh-and-retry behavior without having to touch each call site.
// The refresh API itself uses a separate isolated axios instance
// (see refreshTokenApi.ts), so this does not create a recursion loop.
attachRefreshInterceptor(axios);
